import * as React from 'react';
import classnames from 'classnames';
import { isEmpty } from 'lodash';

import InputArray from 'components/inputs/input-array/InputArray';
import Tooltip from 'components/Tooltip';
import CollapsibleSimple from 'components/collapsible/simple/CollapsibleSimple';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import VerticalSpacing from 'components/vertical-spacing/VerticalSpacing';
import AndOr from 'components/and-or/AndOr';

import DiscoveryFiltersTitle from 'modules/discovery/components/filters/title/DiscoveryFiltersTitle';
import DiscoveryCreatorsFilterPopover from 'modules/freemium/components/discovery-creators-filter-popover/DiscoveryCreatorsFilterPopover';

import { CHANNEL_TYPE_INSTAGRAM } from 'utils/channels';
import { __t, applyTranslations } from 'utils/language';
import { getProfileStatus } from 'modules/discovery/utils/discovery';
import { dataCommonToComponent } from 'utils/data/data';
import {
  UNVERIFIED_DISABLED_PROPS
} from 'modules/discovery/creators/utils/creators';

import './discovery-creators-filters-content.scss';

import type {
  AndOrOptions,
  Data,
  DiscoveryCreatorsFilter,
  DiscoveryCreatorsFilters,
  DiscoveryFiltersTitleSize,
  MixedSelectDropdownItem,
  TooltipTextAlign,
  TooltipWidth
} from 'types';

applyTranslations(require('./discovery-creators-filters-content.i18n.json'));

type Props = {
  filters: DiscoveryCreatorsFilters,
  isFreemium: boolean,
  lastStateReset: number,
  handleDiscoveryFiltersUpdate?: (a: DiscoveryCreatorsFilter) => void
};

const msgKey = 'discovery_creators_filters_content';

class DiscoveryCreatorsFiltersContent extends React.PureComponent<Props> {
  static defaultProps = {
    filters: {},
    handleDiscoveryFiltersUpdate: () => {}
  };

  isLightweightSelected(): boolean {
    const { filters } = this.props;
    const profileStatus = getProfileStatus(filters.profiles, 'creators');

    return profileStatus.unverified;
  }

  isSectionDisabled(identifier: string): boolean {
    const { filters, isFreemium } = this.props;
    const isInstagramEnabled = filters.channels
      .find(channel => channel.identifier === CHANNEL_TYPE_INSTAGRAM)
      ?.selected;

    if ((identifier === 'styles' && !isInstagramEnabled)
      || isFreemium) {
      return true;
    }

    return this.isLightweightSelected() && UNVERIFIED_DISABLED_PROPS.includes(identifier);
  }

  handleItemsUpdate = (updatedItems: Array<MixedSelectDropdownItem>, data?: Data | null): void => {
    const { handleDiscoveryFiltersUpdate } = this.props;

    if (data && data.name && handleDiscoveryFiltersUpdate) {
      handleDiscoveryFiltersUpdate({
        [data.name]: updatedItems
      });
    }
  };

  handleStylesUpdate = (updatedItems: Array<MixedSelectDropdownItem>): void => {
    const { filters, handleDiscoveryFiltersUpdate } = this.props;

    if (handleDiscoveryFiltersUpdate) {
      handleDiscoveryFiltersUpdate({
        styles: filters.styles.map(entry => ({
          ...entry,
          selected: (
            updatedItems.find((item) => item.identifier === entry.identifier) || {}
          ).isSelected
        }))
      });
    }
  };

  handleKeywordsUpdate = (values: Array<string>, data?: Data | null): void => {
    const { filters, handleDiscoveryFiltersUpdate } = this.props;

    if (data && data.name && handleDiscoveryFiltersUpdate) {
      handleDiscoveryFiltersUpdate({
        keywords: {
          ...filters.keywords,
          [data.name]: values
        }
      });
    }
  };

  handleKeywordsMatchUpdate = (option: AndOrOptions): void => {
    const { filters, handleDiscoveryFiltersUpdate } = this.props;

    if (option && handleDiscoveryFiltersUpdate) {
      handleDiscoveryFiltersUpdate({
        keywords: {
          ...filters.keywords,
          matchMode: option
        }
      });
    }
  };

  handleCategoriesUpdate = (updatedItems: Array<MixedSelectDropdownItem>): void => {
    const { filters, handleDiscoveryFiltersUpdate } = this.props;

    if (handleDiscoveryFiltersUpdate) {
      handleDiscoveryFiltersUpdate({
        categories: {
          ...filters.categories,
          items: updatedItems
        }
      });
    }
  };

  handleCategoriesMatchUpdate = (option: AndOrOptions): void => {
    const { filters, handleDiscoveryFiltersUpdate } = this.props;

    if (option && handleDiscoveryFiltersUpdate) {
      handleDiscoveryFiltersUpdate({
        categories: {
          ...filters.categories,
          matchMode: option
        }
      });
    }
  };

  renderFilterPopover(children: React.ReactNode): React.ReactNode {
    return (
      <DiscoveryCreatorsFilterPopover
        triggerComponent={children}
      />
    );
  }

  renderCategoriesSelectDropdownContent(categories) {
    const items = categories.items ?? [];
    const { lastStateReset } = this.props;

    return (
      <MixedSelectDropdown
        key={`discovery-creators-filters-content__categories-${items.length}-${lastStateReset}`}
        name={`discovery-creators-filters-content__categories-${items.length}-${lastStateReset}`}
        items={items}
        withMulti
        withRemovable
        withSortable
        withCheckboxes
        isDisabled={this.isSectionDisabled('categories')}
        onItemsToggle={this.handleCategoriesUpdate}
        onItemsRemove={this.handleCategoriesUpdate}
        onItemsRemoveAll={this.handleCategoriesUpdate}
      />
    );
  }

  renderKeywordsInput(type: 'included' | 'excluded'): React.ReactNode {
    const { filters, isFreemium } = this.props;

    return (
      <InputArray
        key={`discovery-creators-filters-content__keywords-${type}`}
        name={`${type}-keywords`}
        max={20}
        placeholder={__t(`${msgKey}.placeholders.${type}_keywords`)}
        value={filters.keywords[type]}
        supportWhiteSpace
        disabled={isFreemium}
        onChange={this.handleKeywordsUpdate}
        data={{
          parent: 'keywords',
          name: type
        }}
      />
    );
  }

  renderTitle(
    type: string,
    showTooltip?: boolean,
    tooltipWidth: TooltipWidth = 'long',
    titleSize: DiscoveryFiltersTitleSize = 'normal',
    textAlign: TooltipTextAlign = 'center'
  ): React.ReactNode {
    const tooltipClasses = classnames('discovery-creators-filters-content__tooltip', {
      'discovery-creators-filters-content__tooltip--disabled': this.isSectionDisabled(type)
    });

    return (
      <div className="discovery-creators-filters-content__section">
        <div className="discovery-creators-filters-content__title">
          <DiscoveryFiltersTitle
            title={__t(`${msgKey}.labels.${type}`)}
            isDisabled={this.isSectionDisabled(type)}
            size={titleSize}
          />
          {
            showTooltip && (
              <div className={tooltipClasses}>
                <Tooltip
                  message={__t(`${msgKey}.tooltips.${type}`)}
                  width={tooltipWidth}
                  textAlign={textAlign}
                  showQuestionMark
                />
              </div>
            )
          }
        </div>
      </div>
    );
  }

  renderKeywords(): React.ReactNode {
    const { filters, isFreemium } = this.props;

    return (
      <div className="discovery-creators-filters-content__keywords">
        {this.renderTitle('keywords', true, 'extra-long')}
        <div className="discovery-creators-filters-content__subtitle-with-filters">
          {this.renderTitle('included_keywords', true, 'auto-compact', 'small', 'left')}
          <span className="discovery-creators-filters-content__match-block">
            {this.renderTitle('match', true, 'normal', 'small')}
            <AndOr
              name="keywords"
              selectedOption={filters.keywords.matchMode}
              onSetOption={this.handleKeywordsMatchUpdate}
              size="small"
              disabled={isFreemium}
            />
          </span>
        </div>
        {
          isFreemium
            ? this.renderFilterPopover(this.renderKeywordsInput('included'))
            : this.renderKeywordsInput('included')
        }
        <VerticalSpacing type="medium" />
        {this.renderTitle('excluded_keywords', true, 'normal', 'small')}
        {
          isFreemium
            ? this.renderFilterPopover(this.renderKeywordsInput('excluded'))
            : this.renderKeywordsInput('excluded')
        }
      </div>
    );
  }

  renderStylesDropdown(): React.ReactNode {
    const { filters, lastStateReset } = this.props;
    const styles = filters.styles;

    return (
      <MixedSelectDropdown
        key={`discovery-creators-filters-content__styles-${styles.length}-${lastStateReset}`}
        name={`discovery-creators-filters-content__styles-${styles.length}-${lastStateReset}`}
        items={styles.map(dataCommonToComponent)}
        withMulti
        withRemovable
        withSortable
        withCheckboxes
        isDisabled={this.isSectionDisabled('styles')}
        onItemsToggle={this.handleStylesUpdate}
        onItemsRemove={this.handleStylesUpdate}
        onItemsRemoveAll={this.handleStylesUpdate}
      />
    );
  }

  renderStyles(): React.ReactNode | undefined | null {
    const { isFreemium } = this.props;

    return (
      <div className="discovery-creators-filters-content__styles">
        <div className="discovery-creators-filters-content__section">
          <div className="discovery-creators-filters-content__title">
            <DiscoveryFiltersTitle
              title={__t(`${msgKey}.labels.styles`)}
              isDisabled={this.isSectionDisabled('styles')}
            >
              <div className="discovery-creators-filters-content__children">
                {__t(`${msgKey}.labels.beta`)}
              </div>
            </DiscoveryFiltersTitle>
          </div>
        </div>
        {
          isFreemium
            ? this.renderFilterPopover(this.renderStylesDropdown())
            : this.renderStylesDropdown()
        }
      </div>
    );
  }

  renderCategories(): React.ReactNode {
    const { filters, isFreemium } = this.props;

    return (
      <div className="discovery-creators-filters-content__categories">
        <div className="discovery-creators-filters-content__subtitle-with-filters">
          {this.renderTitle('categories', true, 'auto-compact', 'normal', 'left')}
          <span className="discovery-creators-filters-content__match-block">
            {this.renderTitle('match', true, 'normal', 'small')}
            <AndOr
              name="categories"
              selectedOption={filters.categories.matchMode}
              onSetOption={this.handleCategoriesMatchUpdate}
              size="small"
              disabled={isFreemium}
            />
          </span>
        </div>
        {
          isFreemium
            ? this.renderFilterPopover(this.renderCategoriesSelectDropdownContent(filters.categories))
            : this.renderCategoriesSelectDropdownContent(filters.categories)
        }
      </div>
    );
  }

  renderContents(): React.ReactNode {
    return (
      <div className="discovery-creators-filters-content__content">
        {this.renderKeywords()}
        {this.renderCategories()}
        {this.renderStyles()}
      </div>
    );
  }

  render(): React.ReactNode {
    const { filters } = this.props;
    const searchByName = filters.searchByName;

    return (
      <div className="discovery-creators-filters-content">
        <CollapsibleSimple
          title={__t(`${msgKey}.labels.specifications`)}
          isExpanded={isEmpty(searchByName)}
          isDisabled={!isEmpty(searchByName)}
        >
          {this.renderContents()}
        </CollapsibleSimple>
      </div>
    );
  }
}

export default DiscoveryCreatorsFiltersContent;
